import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { gameState } from '../state/gameState.js';
import { lockInput, unlockInput } from './inputLock.js';
import { showToast } from './Toast.js';
import { messageFor } from '../api/errors.js';
import { iconText } from './iconText.js';
import { enlargeForTouch } from './touchEnlarge.js';
import { isTouchDevice } from './isTouchDevice.js';

const GAME_ORDER = ['checkers', 'chess', 'go', 'mancala', 'makao', 'backgammon'];

// Counter shop: six cards (art, Polish name, players, price / owned / coming-soon).
// Buying calls gameState.buyBoardGame and re-renders in place.
export function showGameShopModal(scene) {
  const W = 900, H = 560;
  const cx = GAME_WIDTH / 2, cy = GAME_HEIGHT / 2;
  const layer = scene.add.container(0, 0).setDepth(2000);
  lockInput(scene);

  const overlay = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
    .setOrigin(0, 0).setInteractive();
  const box = scene.add.rectangle(cx, cy, W, H, 0xfdf6e3, 1).setStrokeStyle(4, 0x5b4636);
  const title = iconText(scene, cx, cy - H / 2 + 18, '{dice} Kup gry planszowe', {
    fontFamily: 'system-ui', fontSize: '26px', color: '#333', fontStyle: 'bold',
  }).setOrigin(0.5, 0);
  layer.add([overlay, box, title]);
  enlargeForTouch(scene, layer, { x: cx, y: cy, w: W, h: H }, { overlay });

  const cardsLayer = scene.add.container(0, 0).setDepth(2001);
  layer.add(cardsLayer);

  const renderCards = () => {
    cardsLayer.removeAll(true);
    const catalog = gameState.raw?.catalog?.board_games ?? {};
    const owned = new Set(gameState.raw?.inventory?.board_games ?? []);
    const money = gameState.raw?.money ?? 0;
    const cardW = 270, cardH = 220, gapX = 22, gapY = 24;
    const x0 = cx - W / 2 + 45, y0 = cy - H / 2 + 70;
    GAME_ORDER.forEach((id, i) => {
      const spec = catalog[id];
      if (!spec) return;
      const col = i % 3, row = Math.floor(i / 3);
      const x = x0 + col * (cardW + gapX), y = y0 + row * (cardH + gapY);
      const card = scene.add.rectangle(x + cardW / 2, y + cardH / 2, cardW, cardH, 0xffffff, 1)
        .setStrokeStyle(2, 0xbbaa88);
      const img = scene.add.image(x + cardW / 2, y + 78, `game_${id}`);
      const s = Math.min(150 / img.width, 110 / img.height);
      img.setScale(s);
      if (!spec.implemented) img.setAlpha(0.35);
      const name = iconText(scene, x + cardW / 2, y + 142, spec.name_pl, {
        fontFamily: 'system-ui', fontSize: '20px', color: '#333', fontStyle: 'bold',
      }).setOrigin(0.5, 0);
      const playersTxt = spec.min_players === spec.max_players
        ? `${spec.min_players} graczy`
        : `${spec.min_players}–${spec.max_players} graczy`;
      const players = iconText(scene, x + cardW / 2, y + 166, playersTxt, {
        fontFamily: 'system-ui', fontSize: '14px', color: '#777',
      }).setOrigin(0.5, 0);
      cardsLayer.add([card, img, name, players]);

      let action;
      if (owned.has(id)) {
        action = iconText(scene, x + cardW / 2, y + cardH - 26, 'Posiadasz {check}', {
          fontFamily: 'system-ui', fontSize: '17px', color: '#2e7d32', fontStyle: 'bold',
        }).setOrigin(0.5);
      } else if (!spec.implemented) {
        action = iconText(scene, x + cardW / 2, y + cardH - 26, 'Wkrótce', {
          fontFamily: 'system-ui', fontSize: '17px', color: '#999', fontStyle: 'bold',
        }).setOrigin(0.5);
      } else {
        const afford = money >= spec.price;
        action = iconText(scene, x + cardW / 2, y + cardH - 26, `Kup za ${spec.price} {coin}`, {
          fontFamily: 'system-ui', fontSize: '17px', color: '#fff', fontStyle: 'bold',
          backgroundColor: afford ? '#2e7d32' : '#9e9e9e', padding: { x: 10, y: 4 },
        }).setOrigin(0.5);
        if (afford) {
          const buy = async () => {
            try { await gameState.buyBoardGame(id); renderCards(); }
            catch (e) { showToast(scene, messageFor(e)); }
          };
          action.setInteractive({ useHandCursor: true }).on('pointerup', buy);
          // Phones: the whole card buys — the price label alone was a 49x9 CSS px target.
          if (isTouchDevice()) card.setInteractive({ useHandCursor: true }).on('pointerup', buy);
        }
      }
      cardsLayer.add(action);
    });
  };
  renderCards();

  const closeBtn = iconText(scene, cx + W / 2 - 14, cy - H / 2 + 10, '×', {
    fontFamily: 'system-ui', fontSize: '24px', color: '#a33',
  }).setOrigin(1, 0).setInteractive({ useHandCursor: true }).setDepth(2002);
  layer.add(closeBtn);
  const close = () => { unlockInput(scene); layer.destroy(); };
  closeBtn.on('pointerup', close);
  overlay.on('pointerup', close);
}
